import { useMutation, useQueryClient } from '@tanstack/react-query'
import { $api } from './queryClient'
import { apiClient } from './client'
import type { components } from './schema'

/**
 * The group steering console (spec 2026-09-11-01). Every number the console
 * shows comes from these endpoints — snapshots the jobs computed, never a
 * live table — and every label, definition and target comes from the
 * registry: the front carries no constant of its own.
 */

export type KPIText = components['schemas']['KPIText']
export type KPIDefinition = components['schemas']['SteeringKPIDefinition']
export type KPIFamily = components['schemas']['KPIFamily']
export type KPIRegistry = components['schemas']['KPIRegistryResponse']
export type KPITargets = components['schemas']['KPITargetsResponse']
export type KPITargetEffective = components['schemas']['KPITargetEffective']
export type KPITargetInput = components['schemas']['KPITargetInput']
export type GroupSummary = components['schemas']['GroupSteeringSummary']
export type GroupFamilySummary = components['schemas']['GroupSteeringFamilySummary']
export type GroupTile = components['schemas']['GroupSteeringTile']
export type GroupMatrix = components['schemas']['GroupSteeringMatrix']
export type GroupMatrixRow = components['schemas']['GroupSteeringMatrixRow']
export type GroupCell = components['schemas']['GroupSteeringCell']
export type GroupKPIMeta = components['schemas']['GroupSteeringKPIMeta']
export type GroupPeers = components['schemas']['GroupSteeringPeers']
export type GroupReading = components['schemas']['GroupSteeringReading']
export type GroupException = components['schemas']['GroupSteeringException']
export type GroupExceptions = components['schemas']['GroupSteeringExceptions']
export type GroupTrend = components['schemas']['GroupSteeringTrend']
export type GroupTrendPoint = components['schemas']['GroupSteeringTrendPoint']
export type GroupPerimeterSheet = components['schemas']['GroupSteeringPerimeterSheet']
export type GroupPerimeterRef = components['schemas']['GroupSteeringPerimeterRef']
export type GroupMap = components['schemas']['GroupSteeringMap']
export type GroupMapPoint = components['schemas']['GroupSteeringMapPoint']
export type GroupTerritories = components['schemas']['GroupSteeringTerritories']
export type GroupGovernance = components['schemas']['GroupSteeringGovernance']
export type GroupFreshness = components['schemas']['GroupSteeringFreshness']
export type GroupSites = components['schemas']['GroupSteeringSites']
export type GroupSiteRow = components['schemas']['GroupSteeringSiteRow']
export type GroupSiteCell = components['schemas']['GroupSteeringSiteCell']
export type GroupBreakdownItem = components['schemas']['GroupSteeringBreakdownItem']

export type Period = '30d' | '90d' | '12m'
export type Level = 'entity' | 'agency' | 'portfolio'
export type Tier = 'excellent' | 'healthy' | 'watch' | 'none'

export const PERIODS: Period[] = ['30d', '90d', '12m']
export const LEVELS: Level[] = ['entity', 'agency', 'portfolio']

const BASE = '/api/v1/organizations/{orgCode}/steering/group'

export function useKPIRegistry(org: string) {
    return $api.useQuery('get', '/api/v1/organizations/{orgCode}/kpis', { params: { path: { orgCode: org } } }, { staleTime: 10 * 60 * 1000 })
}

export function useKPITargets(org: string, enabled = true) {
    return $api.useQuery('get', '/api/v1/organizations/{orgCode}/kpi-targets', { params: { path: { orgCode: org } } }, { enabled })
}

export function useGroupSummary(org: string, period: Period, level: Level) {
    return $api.useQuery('get', `${BASE}/summary`, { params: { path: { orgCode: org }, query: { period, level } } }, { retry: false })
}

export function useGroupMatrix(org: string, period: Period, level: Level, family?: string) {
    return $api.useQuery(
        'get',
        `${BASE}/matrix`,
        { params: { path: { orgCode: org }, query: { period, level, family: family || undefined } } },
        { retry: false },
    )
}

export function useGroupExceptions(org: string, period: Period, level: Level, family?: string, enabled = true) {
    return $api.useQuery(
        'get',
        `${BASE}/exceptions`,
        { params: { path: { orgCode: org }, query: { period, level, family: family || undefined } } },
        { retry: false, enabled },
    )
}

export function useGroupTrend(org: string, code: string | undefined, level: Level, perimeter?: string, months = 12) {
    return $api.useQuery(
        'get',
        `${BASE}/kpis/{code}/trend`,
        { params: { path: { orgCode: org, code: code ?? '' }, query: { level, perimeter: perimeter || undefined, months } } },
        { retry: false, enabled: !!code },
    )
}

export function useGroupPerimeter(org: string, uid: string | undefined, period: Period, family: string) {
    return $api.useQuery(
        'get',
        `${BASE}/perimeters/{perimeterUid}`,
        { params: { path: { orgCode: org, perimeterUid: uid ?? '' }, query: { period, family } } },
        { retry: false, enabled: !!uid },
    )
}

export function useGroupMap(org: string, kpi: string | undefined, period: Period) {
    return $api.useQuery('get', `${BASE}/map`, { params: { path: { orgCode: org }, query: { kpi, period } } }, { retry: false, enabled: !!kpi })
}

export function useGroupTerritories(org: string, kpi: string | undefined, period: Period, level: 'country' | 'admin_area_1' = 'admin_area_1') {
    return $api.useQuery(
        'get',
        `${BASE}/territories`,
        { params: { path: { orgCode: org }, query: { kpi, period, level } } },
        { retry: false, enabled: !!kpi },
    )
}

export function useGroupGovernance(org: string, period: Period, enabled = true) {
    return $api.useQuery('get', `${BASE}/governance`, { params: { path: { orgCode: org }, query: { period } } }, { retry: false, enabled })
}

/**
 * The per-site snapshot of an indicator flagged `per_site` (the NPS today,
 * spec 2026-10-02-02): the pooled row of the scope and one row per site,
 * most answered first, each with its composition.
 */
export function useGroupSites(org: string, period: Period, perimeter?: string, kpi = 'nps') {
    return $api.useQuery(
        'get',
        `${BASE}/sites`,
        { params: { path: { orgCode: org }, query: { kpi, period, perimeter: perimeter || undefined } } },
        { retry: false },
    )
}

export function useGroupMutations(org: string) {
    const queryClient = useQueryClient()
    const invalidate = () =>
        queryClient.invalidateQueries({
            predicate: (q) => {
                const key = q.queryKey as unknown[]
                const path = typeof key[1] === 'string' ? key[1] : ''
                return path.includes('/steering/group') || path.endsWith('/kpi-targets')
            },
        })
    const setTargets = useMutation({
        mutationFn: async (targets: KPITargetInput[]) => {
            const res = await apiClient.PUT('/api/v1/organizations/{orgCode}/kpi-targets', {
                params: { path: { orgCode: org } },
                body: { targets },
            })
            if (res.error) throw new Error((res.error as { title?: string }).title ?? 'targets')
            return res.data
        },
        onSuccess: invalidate,
    })
    // The recompute is a job: the request answers 202 and the snapshot lands
    // later. The mutation then polls the summary's freshness until its
    // computed_at moves (two minutes at most) and invalidates every console
    // query, so the pages redraw on the new day without a reload.
    const recompute = useMutation({
        mutationFn: async () => {
            const before = await apiClient.GET(`${BASE}/summary`, { params: { path: { orgCode: org }, query: { period: '90d', level: 'agency' } } })
            const stamp = before.data?.freshness?.computed_at ?? null
            const res = await apiClient.POST(`${BASE}/recompute`, { params: { path: { orgCode: org } } })
            if (res.error) throw new Error((res.error as { title?: string }).title ?? 'recompute')
            for (let i = 0; i < 40; i++) {
                await new Promise((r) => setTimeout(r, 3000))
                const after = await apiClient.GET(`${BASE}/summary`, { params: { path: { orgCode: org }, query: { period: '90d', level: 'agency' } } })
                const now = after.data?.freshness?.computed_at ?? null
                if (now && now !== stamp) return true
            }
            return false
        },
        onSettled: invalidate,
    })
    return { setTargets, recompute, invalidate }
}
